import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Badge } from '../../components/Badge/Badge';
import { Consignment } from '../../components/Consignment/Consignment';
import { Icon, delivery as deliveryIcon } from '../../components/Icon/Icon';
import { OrderItemNotConsigned } from '../../components/OrderItemNotConsigned/OrderItemNotConsigned';
import { useTranslation } from '../../i18n/i18n';
import { Res, useApi } from '../../state/api';
import { AccountPageNavigation } from '../AccountPageNavigation/AccountPageNavigation';
import { Layout } from '../Layout/Layout';
import { Main } from '../Main/Main';
import { Routing } from '../Routes';
import { ErrorPage } from './ErrorPage';
import { LoaderPage } from './LoaderPage';
import './OrderFullfilmentPage.css';

export const OrderFulfillment = (): JSX.Element => {
	const locationState = useLocation<{ displayId: string }>().state;
	type UrlParams = { orderId: string };
	const { orderId } = useParams<UrlParams>();
	const { data: order, error: orderError } = useApi(Res.order, { orderId });
	const { data: fulfillment, error: fulfillmentError } = useApi(Res.orderFulfillment, { orderId });
	const t = {
		...useTranslation().orderPages,
		...useTranslation().orderFulfillment,
		paymentType: useTranslation().paymentType,
		shippingType: useTranslation().shippingType,
	};

	const [fullyShipped, partShipped] = useMemo(() => {
		let fullyShipped = false;
		let partShipped = false;

		if (order) {
			order.events.forEach((event) => {
				if (event.status == 3) {
					fullyShipped = true;
				} else if (event.status == 8) {
					partShipped = true;
				}
			});
		}

		return [fullyShipped, partShipped];
	}, [order]);

	const [leftByTcId, anythingLeft] = useMemo(() => {
		let leftByTcId: Record<number, number> = {};
		let anythingLeft = false;

		order?.items.forEach((orderItem) => {
			leftByTcId[orderItem.product.tcId] = orderItem.quantity;
		});

		fulfillment?.involvedVendors.forEach((involvedVendor) => {
			involvedVendor.consignments.forEach((consignment) => {
				consignment.articles.forEach((consignedArticle) => {
					leftByTcId[consignedArticle.productId] -= consignedArticle.quantity;
				});
			});
		});

		Object.entries(leftByTcId).forEach(([, leftCount]) => {
			if (leftCount > 0) {
				anythingLeft = true;
			}
		});

		return [leftByTcId, anythingLeft];
	}, [order, fulfillment]);

	if (orderError || fulfillmentError) return <ErrorPage />;
	if (!order) return <LoaderPage />;

	if (!fulfillment) return <Main>{t.noInfo}</Main>;

	return (
		<Main>
			<AccountPageNavigation />
			<div id="order-fullfilment-page">
				<h1>{`${t.deliveryOfOrder} ${locationState ? locationState.displayId : order.id}`}</h1>
				<Layout type="two-pane">
					<dl className="order-fullfilment-details">
						<dt>
							{fullyShipped && <Badge color="blue">{t.fullyShipped}</Badge>}
							{partShipped && !fullyShipped && <Badge color="orange">{t.partShipped}</Badge>}
						</dt>
						<dt>
							{t.deliveryMethod}: <strong>{t.shippingType[order.shippingType]}</strong>
						</dt>
						{order.paymentType && (
							<dt>
								{t.paymentMethod}:{' '}
								<strong>{order.paymentDescription || t.paymentType[order.paymentType]}</strong>
							</dt>
						)}
					</dl>
					{false && ( //prepared but no time to build this now
						<div>
							<h2>
								<Icon shape={deliveryIcon}></Icon> Din order skickas från flera lager
							</h2>
							<p>Lite infotext...</p>
						</div>
					)}
				</Layout>

				{fulfillment?.involvedVendors.map((involvedVendor) =>
					involvedVendor?.consignments?.map((consignment) => (
						<Consignment
							key={consignment.id}
							consignment={consignment}
							vendorId={involvedVendor.id}
							orderItems={order.items}
						></Consignment>
					))
				)}

				{anythingLeft && (
					<>
						<h2>{t.notYetSent}</h2>
						{order.items.map((orderItem) => {
							const left = leftByTcId[orderItem.product.tcId];
							if (left > 0)
								return <OrderItemNotConsigned key={orderItem.id} orderItem={orderItem} left={left} />;
						})}
					</>
				)}
			</div>
		</Main>
	);
};

export const orderFulfillmentRouting: Routing = {
	path(urls) {
		return `${urls.yourOrders}/:orderId/${urls.fulfillment}`;
	},
	component: OrderFulfillment,
	requiresAuth: true,
};
